import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../components/web3/context/AuthContext";
import { getHnsProfile, setHnsName, releaseHnsName, resolveHnsName } from "../../api/mcpApi";
import { toast } from "react-hot-toast";
import ReactGA from "react-ga4";
import "../../assets/styles/css/names.css";

const USERNAME_RE = /^[a-z][a-z0-9_]{1,18}[a-z0-9]$/;
const RESERVED_NAMES = new Set([
  "admin", "horus", "system", "support", "wallet", "help",
  "official", "team", "bot", "api", "root", "null", "undefined",
  "test", "demo", "mod",
]);

const NAMING_SERVICES = [
  { name: "HNS", suffix: "@username", desc: "Horus Name Service" },
  { name: "ENS", suffix: ".eth", desc: "Ethereum Name Service" },
  { name: "Basenames", suffix: ".base.eth", desc: "Base Name Service" },
  { name: "Space ID", suffix: ".bnb / .arb", desc: "BNB & Arbitrum" },
  { name: "Avvy", suffix: ".avax", desc: "Avalanche Domains" },
  { name: "Unstoppable", suffix: ".crypto / .nft / .wallet", desc: "Unstoppable Domains" },
];

function truncateAddress(addr, start = 6, end = 4) {
  if (!addr) return "";
  if (addr.length <= start + end) return addr;
  return `${addr.slice(0, start)}...${addr.slice(-end)}`;
}

function avatarColor(username) {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#2563eb", "#7c3aed", "#db2777", "#dc2626",
    "#ea580c", "#ca8a04", "#16a34a", "#0891b2",
  ];
  return colors[Math.abs(hash) % colors.length];
}

export default function NamesPage() {
  const { isLoggedIn, entityInfo } = useAuth();
  const walletIndex = entityInfo?.indexWallet ?? 0;

  const [headerHeight, setHeaderHeight] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Registration form
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);

  // Lookup
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    const header = document.getElementById("sticky-menu");
    if (header) setHeaderHeight(header.offsetHeight);
  }, []);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/names", title: "Names Page" });
  }, []);

  const loadProfile = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoading(true);
    try {
      const data = await getHnsProfile(walletIndex);
      setProfile(data);
    } catch {
      setProfile({ registered: false });
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, walletIndex]);

  useEffect(() => {
    loadProfile();
    // Reset editing state when wallet changes
    setEditing(false);
    setConfirmRelease(false);
  }, [loadProfile]);

  const validateUsername = (val) => {
    if (!val) { setValidationError(""); return; }
    if (val.length < 3) { setValidationError("Min 3 characters"); return; }
    if (val.length > 20) { setValidationError("Max 20 characters"); return; }
    if (!/^[a-z]/.test(val)) { setValidationError("Must start with a letter"); return; }
    if (!/[a-z0-9]$/.test(val)) { setValidationError("Must end with letter or number"); return; }
    if (!/^[a-z0-9_]+$/.test(val)) { setValidationError("Only lowercase letters, numbers, underscores"); return; }
    if (RESERVED_NAMES.has(val)) { setValidationError(`"${val}" is reserved`); return; }
    setValidationError("");
  };

  const handleUsernameChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setUsername(val);
    validateUsername(val);
  };

  const handleRelease = async () => {
    setReleasing(true);
    try {
      await releaseHnsName(walletIndex);
      toast.success(`Released @${profile.username}`);
      setConfirmRelease(false);
      setEditing(false);
      loadProfile();
      window.dispatchEvent(new CustomEvent("hns-name-changed", { detail: { username: null } }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to release name");
    } finally {
      setReleasing(false);
    }
  };

  const handleRegister = async () => {
    if (!USERNAME_RE.test(username)) {
      toast.error("Invalid username format");
      return;
    }
    if (RESERVED_NAMES.has(username)) {
      toast.error(`"${username}" is reserved`);
      return;
    }
    setSaving(true);
    try {
      // Backend auto-releases old name when wallet registers a new one
      await setHnsName(username, displayName.trim() || undefined, walletIndex);
      toast.success(`Registered as @${username}`);
      setEditing(false);
      loadProfile();
      window.dispatchEvent(new CustomEvent("hns-name-changed", { detail: { username } }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to register name");
    } finally {
      setSaving(false);
    }
  };

  const normalizeLookupQuery = (input) => {
    let q = input.trim().toLowerCase();
    // Strip .horus suffix → treat as HNS
    if (q.endsWith(".horus")) {
      q = q.slice(0, -6);
    }
    // If no @ prefix, no dot (not ENS/etc), and not 0x address → treat as HNS @username
    if (!q.startsWith("@") && !q.startsWith("0x") && !q.includes(".")) {
      q = `@${q}`;
    }
    return q;
  };

  const handleLookup = async () => {
    const q = normalizeLookupQuery(lookupQuery);
    if (!q) return;
    setLookupLoading(true);
    setLookupError("");
    setLookupResult(null);
    try {
      const data = await resolveHnsName(q);
      setLookupResult(data);
    } catch (err) {
      setLookupError(err?.response?.data?.message || "Name not found");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const isRegistered = profile?.registered && profile?.username;
  const walletAddress = entityInfo?.activePublicKey;

  if (!isLoggedIn) {
    return (
      <div className="names-page" style={{ paddingTop: headerHeight }}>
        <div className="names-container">
          <div className="names-login-prompt">
            <h3>Sign in to manage your @username</h3>
            <p>Connect your wallet or sign in to register a Horus Name.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="names-page" style={{ paddingTop: headerHeight }}>
      <div className="names-container">
        <div className="names-header">
          <h2 className="names-title">Names</h2>
          <p className="names-subtitle">
            Register your @username on the Horus Name Service
          </p>
        </div>

        {/* ── Section 1: My @username ──────────────────────────── */}
        <div className="names-section">
          <h3 className="names-section-title">My @username</h3>
          {loading ? (
            <div className="names-skeleton">
              <div className="names-skeleton-line" style={{ width: "60%" }} />
              <div className="names-skeleton-line" style={{ width: "40%" }} />
            </div>
          ) : isRegistered && !editing ? (
            <>
            <div className="names-profile-card">
              <div
                className="names-avatar"
                style={{ background: avatarColor(profile.username) }}
              >
                {profile.username.charAt(0).toUpperCase()}
              </div>
              <div className="names-profile-info">
                <span className="names-username">@{profile.username}</span>
                {profile.display_name && (
                  <span className="names-display-name">
                    {profile.display_name}
                  </span>
                )}
                <span className="names-address">
                  {truncateAddress(profile.default_address || walletAddress)}
                  <button
                    type="button"
                    className="names-copy-btn-inline"
                    onClick={() =>
                      handleCopy(profile.default_address || walletAddress)
                    }
                  >
                    Copy
                  </button>
                </span>
                {profile.created_at && (
                  <span className="names-meta">
                    Registered{" "}
                    {new Date(profile.created_at).toLocaleDateString()}
                  </span>
                )}
                {profile.chain_count > 0 && (
                  <span className="names-meta">
                    Addresses on {profile.chain_count} chains
                  </span>
                )}
              </div>
              <div className="names-profile-actions">
                <button
                  type="button"
                  className="names-btn names-btn-sm"
                  onClick={() => {
                    setEditing(true);
                    setUsername("");
                    setDisplayName(profile.display_name || "");
                  }}
                >
                  Change
                </button>
                <button
                  type="button"
                  className="names-btn names-btn-sm names-btn-danger"
                  onClick={() => setConfirmRelease(true)}
                >
                  Release
                </button>
              </div>
            </div>

            {/* Release confirmation */}
            {confirmRelease && (
              <div className="names-confirm-banner">
                <p>
                  Release <strong>@{profile.username}</strong>? This will free
                  the username for anyone to claim.
                </p>
                <div className="names-confirm-actions">
                  <button
                    type="button"
                    className="names-btn names-btn-danger"
                    onClick={handleRelease}
                    disabled={releasing}
                  >
                    {releasing ? "Releasing..." : "Yes, release"}
                  </button>
                  <button
                    type="button"
                    className="names-btn names-btn-outline"
                    onClick={() => setConfirmRelease(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="names-register-form">
              {!isRegistered && (
                <p className="names-form-desc">
                  Claim your @username for wallet{" "}
                  {truncateAddress(walletAddress)}
                </p>
              )}
              <div className="names-field">
                <label className="names-label">Username</label>
                <div className="names-input-prefix-wrap">
                  <span className="names-input-prefix">@</span>
                  <input
                    type="text"
                    className="names-input names-input-with-prefix"
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="username"
                    maxLength={20}
                    autoFocus
                  />
                </div>
                {validationError && (
                  <span className="names-field-error">{validationError}</span>
                )}
                <span className="names-field-hint">
                  3-20 chars, starts with letter, lowercase letters/numbers/underscores
                </span>
              </div>
              <div className="names-field">
                <label className="names-label">Display name (optional)</label>
                <input
                  type="text"
                  className="names-input"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your display name"
                />
              </div>
              <div className="names-form-actions">
                <button
                  type="button"
                  className="names-btn names-btn-primary"
                  onClick={handleRegister}
                  disabled={
                    saving || !username.trim() || !!validationError
                  }
                >
                  {saving
                    ? "Registering..."
                    : isRegistered
                    ? "Update"
                    : `Register @${username || "username"}`}
                </button>
                {editing && (
                  <button
                    type="button"
                    className="names-btn names-btn-outline"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Section 2: Look up any name ─────────────────────── */}
        <div className="names-section">
          <h3 className="names-section-title">Look up any name</h3>
          <p className="names-section-desc">
            Resolve @usernames, ENS, Basenames, Space ID, Avvy, and
            Unstoppable Domains.
          </p>
          <div className="names-lookup-row">
            <input
              type="text"
              className="names-input"
              value={lookupQuery}
              onChange={(e) => setLookupQuery(e.target.value)}
              placeholder="@akram, vitalik.eth, akram.bnb..."
              onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            />
            <button
              type="button"
              className="names-btn names-btn-primary"
              onClick={handleLookup}
              disabled={lookupLoading || !lookupQuery.trim()}
            >
              {lookupLoading ? "Resolving..." : "Look up"}
            </button>
          </div>

          {lookupError && (
            <div className="names-lookup-error">{lookupError}</div>
          )}

          {lookupResult && (
            <div className="names-lookup-result">
              <div className="names-lookup-result-header">
                <span className="names-lookup-name">{lookupResult.name}</span>
                <span className="names-badge names-badge-blue">
                  {lookupResult.service?.toUpperCase() || "HNS"}
                </span>
                {lookupResult.chain && (
                  <span className="names-badge names-badge-gray">
                    {lookupResult.chain}
                  </span>
                )}
              </div>
              <div className="names-lookup-address">
                <code>{lookupResult.address}</code>
                <button
                  type="button"
                  className="names-copy-btn-inline"
                  onClick={() => handleCopy(lookupResult.address)}
                >
                  Copy
                </button>
              </div>
              <a
                href={`/transferDashboard?to=${lookupResult.address}`}
                className="names-btn names-btn-sm names-btn-link"
              >
                Send to this address &rarr;
              </a>
            </div>
          )}

          {/* Supported services */}
          <div className="names-services-grid">
            {NAMING_SERVICES.map((s) => (
              <div key={s.name} className="names-service-card">
                <span className="names-service-name">{s.name}</span>
                <span className="names-service-suffix">{s.suffix}</span>
                <span className="names-service-desc">{s.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
